/**
 * usePendingUploads — the single source of truth for "what is still waiting to
 * upload from this device" (Phase 14.6).
 *
 * Both the dashboard status card and the Pending Uploads screen read this hook,
 * so their counts can never disagree. It combines:
 *   - the offline sync queue (offlineDb / syncEngine)
 *   - the durable appraisal photo upload queue
 *
 * It does not change any upload behaviour — it only observes.
 */
import { useCallback, useEffect, useState } from "react";
import { syncQueue, type SyncQueueItem } from "@/lib/offlineDb";
import { getSyncStatus, onSyncStatusChange, type SyncStatus } from "@/lib/syncEngine";
import {
  getPendingUploadItems,
  onAppraisalUploadChange,
  type PendingAppraisalUploadItem,
} from "@/lib/appraisalUploadQueue";
import { isUserActionableFailureKind } from "@/lib/userActionableUploads";

/** Uploads are only considered "stuck" after this long with a live connection. */
export const STUCK_AFTER_MS = 30 * 60 * 1000;
/** ...and only once the engine has genuinely retried the work. */
export const STUCK_MIN_ATTEMPTS = 3;

export interface PendingUploadsState {
  syncItems: SyncQueueItem[];
  photoItems: PendingAppraisalUploadItem[];
  /** Combined outstanding item count shown to the user. */
  total: number;
  syncStatus: SyncStatus;
  loading: boolean;
  refresh: () => Promise<void>;
  /** Timestamp of the longest-waiting queued item, or null when nothing is queued. */
  oldestPendingAt: number | null;
  /**
   * True only when online work has repeatedly failed for an extended period.
   *
   * INTERNAL SIGNAL ONLY (1.67.x). It feeds telemetry and support tooling. It
   * is deliberately NOT what the home screen shows, because "the system is
   * having trouble" is not something the user can act on.
   */
  stuck: boolean;
  /**
   * True only when a queued item has failed in a way the USER can resolve
   * (currently: the device is out of storage). Everything else — retries,
   * offline waits, RLS/authorisation denials, verification problems — is
   * handled internally and stays invisible to the end user.
   */
  userActionable: boolean;
}

export function usePendingUploads(options: { pollMs?: number } = {}): PendingUploadsState {
  const { pollMs = 3000 } = options;
  const [syncItems, setSyncItems] = useState<SyncQueueItem[]>([]);
  const [photoItems, setPhotoItems] = useState<PendingAppraisalUploadItem[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(getSyncStatus());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const [items, photos] = await Promise.all([
      syncQueue.getAll().catch(() => [] as SyncQueueItem[]),
      getPendingUploadItems().catch(() => [] as PendingAppraisalUploadItem[]),
    ]);
    items.sort((a, b) => {
      if (a.attempts > 0 && b.attempts === 0) return -1;
      if (a.attempts === 0 && b.attempts > 0) return 1;
      return a.created_at - b.created_at;
    });
    setSyncItems(items);
    setPhotoItems(photos.sort((a, b) => a.createdAt - b.createdAt));
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = () => { if (mounted) void refresh(); };
    run();

    const unsubscribeSync = onSyncStatusChange((next) => {
      setSyncStatus(next);
      run();
    });
    const unsubscribePhotos = onAppraisalUploadChange(() => run());
    const interval = pollMs > 0 ? setInterval(run, pollMs) : null;

    return () => {
      mounted = false;
      unsubscribeSync();
      unsubscribePhotos();
      if (interval) clearInterval(interval);
    };
  }, [refresh, pollMs]);

  // Phase 14.7 — "stuck" means: still queued, already retried, and the oldest
  // item has been waiting far longer than any normal retry cycle. Transient
  // signal loss and ordinary backoff never satisfy this.
  const now = Date.now();
  const oldestAt = Math.min(
    ...syncItems.map((i) => i.created_at),
    ...photoItems.map((i) => i.createdAt),
    Number.POSITIVE_INFINITY,
  );
  const hasRetried =
    syncItems.some((i) => i.attempts >= STUCK_MIN_ATTEMPTS) ||
    photoItems.some((i) => i.attempts >= STUCK_MIN_ATTEMPTS);
  const oldestPendingAt = Number.isFinite(oldestAt) ? oldestAt : null;
  const stuck =
    typeof navigator !== "undefined" &&
    navigator.onLine !== false &&
    hasRetried &&
    oldestPendingAt !== null &&
    now - oldestPendingAt > STUCK_AFTER_MS;

  // 1.67.x — user-actionable is a strictly narrower thing than "stuck".
  const userActionable = photoItems.some((i) => isUserActionableFailureKind(i.lastErrorKind));

  return {
    syncItems,
    photoItems,
    total: syncItems.length + photoItems.length,
    syncStatus,
    loading,
    refresh,
    oldestPendingAt,
    stuck,
    userActionable,
  };
}
